import { computed, type Ref } from 'vue';
import Split from 'split.js';
import { onMounted, watch, nextTick } from 'vue';
import { useEventListener, useLocalStorage } from '@vueuse/core';
import { throttle } from 'lodash-es';

export const layout = useLocalStorage<EditorLayout>('layout', 'pane-1');

const dispatchResize = () => window.dispatchEvent(new Event('resize'));
const dispatchResizeThrottled = throttle(dispatchResize, 10);

export const setLayout = (v: EditorLayout) => {
  layout.value = v;
  dispatchResize();
};

export const savedPaneWidths = useLocalStorage<EditorPaneSettings>(
  'paneSettings',
  {
    'pane-0': { widths: [100] },
    'pane-1': { widths: [50, 50] },
    'pane-2': { widths: [33.3333, 33.3333, 33.3333] },
    'pane-3': { widths: [50, 50] },
    'popup-1': { widths: [100] },
    'popup-2': { widths: [50, 50] }
  }
);

export const setPaneWidths = (widths: number[]) => {
  if (layout.value === 'pane-0') return;
  if (widths.some(w => w === null || w === undefined || Number.isNaN(w)))
    return;
  savedPaneWidths.value[layout.value].widths = widths;
};

export const paneWidths = computed(
  () => savedPaneWidths.value[layout.value].widths
);

export const fallbackToPanePreview = () => {
  if (layout.value === 'popup-1') {
    layout.value = 'pane-1';
  } else if (layout.value === 'popup-2') {
    layout.value = 'pane-2';
  }
};

export default function usePanes(elRef: Ref<HTMLElement | undefined>) {
  const hasPopup = computed(() => layout.value.includes('popup'));

  const initPanes = async () => {
    await nextTick();
    const el = elRef.value;
    if (!el) return;

    const panes = [
      ...el.querySelectorAll<HTMLDivElement>(
        '.author-editor-pane, .author-editor-preview'
      )
    ];

    panes.forEach((pane, i) => {
      pane.style.width = `${paneWidths.value[i]}%`;
    });

    // We only want to set the panes if we move the gutter splitters
    if (panes.length === 0 || panes.length === 1) return;

    Split(panes, {
      gutterSize: 0,
      sizes: [...paneWidths.value],
      onDrag(sizes) {
        dispatchResizeThrottled();
      },
      onDragEnd(sizes) {
        setPaneWidths(sizes);
      }
    });
  };

  onMounted(initPanes);
  watch(layout, initPanes);

  return { hasPopup };
}

export const usePopup = (
  { courseId, firmCode }: CourseAndFirm,
  draft: Ref<CourseDraft | undefined>
) => {
  let popup: Window | null;
  const hasPopup = computed(() => layout.value.includes('popup'));

  watch(
    hasPopup,
    v => {
      if (!v) {
        popup?.close();
        return;
      }

      popup = window.open(
        `#/preview/${firmCode ? `${firmCode}_` : ''}${courseId}`,
        `Preview: ${draft.value?.title ?? 'Untitled'}`,
        'width=600,height=600,left=100,top=100'
      );

      popup?.addEventListener('beforeunload', fallbackToPanePreview);
      if (!popup) fallbackToPanePreview();
    },
    { immediate: true }
  );

  useEventListener('beforeunload', () => {
    popup?.close();
  });
};
